/**
 * BottomSheet.jsx
 * Шторка снизу с корректным свайпом, отскоком и независимым скроллом контента.
 */

import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import IconButton from "@mui/joy/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { AnimatePresence, motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useRef } from "react";
import { Stack } from "@mui/joy";

export default function BottomSheet({ open, onClose, title, children, subTitle, footer }) {
  const y = useMotionValue(0);

  // Оверлей плавно исчезает по мере свайпа вниз
  const overlayOpacity = useTransform(y, [0, 300], [1, 0]);

  const handleDragEnd = (_, info) => {
    const offset   = info.offset.y;
    const velocity = info.velocity.y;

    const shouldClose =
      offset > 180 ||
      (offset > 50 && velocity > 600);

    if (shouldClose) {
      animate(y, window.innerHeight, {
        type: "spring",
        stiffness: 300,
        damping: 30,
        onComplete: onClose,
      });
    } else {
      // Отскок — пружина с небольшим перелётом
      animate(y, 0, {
        type: "spring",
        stiffness: 520,
        damping: 28,
        mass: 0.7,
        velocity: info.velocity.y,
      });
    }
  };

  return (
    <AnimatePresence onExitComplete={() => y.set(0)}>
      {open && (
        <>
          {/* ── Оверлей ── */}
          <motion.div
            key="bs-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1299,
              backdropFilter: "blur(5px)",
              WebkitBackdropFilter: "blur(5px)",
              backgroundColor: "rgba(15,15,35,0.48)",
              opacity: overlayOpacity,
            }}
          />

          {/* ── Шторка ── */}
          <motion.div
            key="bs-panel"
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0.04, bottom: 1 }}
            style={{
              y,
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1300,
              height: "90dvh",
              borderRadius: "20px 20px 0 0",
              backgroundColor: "var(--joy-palette-background-surface, #fff)",
              boxShadow: "0 -8px 48px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              // touch-action: none — чтобы drag работал на всей шторке
              touchAction: "none",
            }}
            onDragEnd={handleDragEnd}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{
              type: "spring",
              stiffness: 380,
              damping: 36,
              mass: 0.75,
            }}
          >
            {/* ── Ручка ── */}
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                pt: 1.5,
                pb: 1,
                cursor: "grab",
                "&:active": { cursor: "grabbing" },
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 4.5,
                  borderRadius: 8,
                  bgcolor: "color-mix(in srgb, var(--joy-palette-neutral-400) 40%, transparent)",
                }}
              />
            </Box>

            {/* ── Заголовок ── */}
            <Box
              sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                pb: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction={"column"} sx={{ gap: 1 }}>
                <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                  {title}
                </Typography>
                { subTitle && (
                  <Typography level="body-xs" sx={{ lineHeight: 1.1, fontWeight: 200 }}>
                    {subTitle}
                  </Typography>
                )}
              </Stack>
              
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={onClose}
                // кнопке разрешаем нормальный клик
                onPointerDown={(e) => e.stopPropagation()}
                sx={{ touchAction: "auto" }}
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* ── Контент — перехватываем pointer чтобы скролл работал ── */}
            <Box
              onPointerDown={(e) => e.stopPropagation()}
              sx={{
                flex: 1,
                overflowY: "auto",
                touchAction: "pan-y",
                WebkitOverflowScrolling: "touch",
                userSelect: "text",
                "&::-webkit-scrollbar": { width: "3px" },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "color-mix(in srgb, var(--joy-palette-primary-400) 28%, transparent)",
                  borderRadius: "4px",
                },
              }}
            >
              {children}
            </Box>

            {/* ── Закреплённый низ (не скроллится) ── */}
            {footer && (
              <Box
                onPointerDown={(e) => e.stopPropagation()}
                sx={{ flexShrink: 0, touchAction: "auto" }}
              >
                {footer}
              </Box>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}